import React from 'react';
import { fmtMoney, fmtNum, fmtDate } from '../lib/utils';
import PrintHeader, { DEFAULT_COMPANY } from './PrintHeader';

const BLUE = '#1E3A8A';

function bookAmt(v) {
  const n = Number(v || 0);
  return n % 1 ? n.toFixed(3) : n.toLocaleString('en-US');
}

function bookDate(d) {
  const dt = new Date(d);
  return `${dt.getDate()}/${dt.getMonth() + 1}/${String(dt.getFullYear()).slice(2)}`;
}

function BookBlocks({ blocks, grandTotal, currency }) {
  const colCount = 3;
  const per = Math.ceil(blocks.length / colCount) || 1;
  const cols = Array.from({ length: colCount }, (_, i) => blocks.slice(i * per, (i + 1) * per));
  return (
    <div className="mb-6">
      <div className="flex gap-0 border" style={{ borderColor: BLUE }}>
        {cols.map((col, ci) => (
          <div key={ci} className="flex-1 px-4 py-3" style={{ borderLeft: ci ? `1px solid ${BLUE}` : 'none', minHeight: 60 }}>
            {col.map((b) => (
              <div key={b.date} className="mb-5" data-testid="print-book-block">
                <p className="text-center font-heading font-bold text-slate-900 border-b-2 border-slate-800 w-fit mx-auto px-3 pb-0.5 mb-2 num text-[15px]">{bookDate(b.date)}</p>
                {b.entries.map((e, i) => (
                  <div key={i} className="flex items-baseline gap-2 py-0.5" style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <span className="num font-semibold text-slate-900 w-20 text-right text-[14px]">{bookAmt(e.amount)}</span>
                    <span className="text-slate-400">—</span>
                    <span className="text-[13px] text-slate-700">{e.collectedBy}</span>
                  </div>
                ))}
                <div className="flex items-baseline gap-2 pt-1 mt-0.5" style={{ borderTop: '2px solid #334155' }}>
                  <span className="num font-bold text-slate-900 w-20 text-right text-[15px]" style={{ borderBottom: '3px double #334155' }}>{bookAmt(b.total)}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="flex justify-end items-baseline gap-3 mt-3 pr-2" style={{ color: BLUE }}>
        <span className="text-sm font-semibold">Grand Total · <span className="font-arabic">الإجمالي</span>:</span>
        <span className="num font-bold text-[17px] text-slate-900" style={{ borderBottom: '3px double #334155' }}>{fmtMoney(grandTotal, currency)}</span>
      </div>
    </div>
  );
}

export default function PaymentsLedgerPrint({ groupLabel, periodLabel, rows, collectors, totals, grandTotal, company, blocks }) {
  const c = { ...DEFAULT_COMPANY, ...(company || {}) };
  return (
    <div className="bg-white text-slate-900 font-sans p-4" style={{ minWidth: 640 }}>
      <PrintHeader company={c} />

      <div className="flex items-center justify-between my-5" style={{ color: BLUE }}>
        <div className="text-sm w-44">
          <p className="font-semibold">Statement Date</p>
          <p className="num text-[15px] mt-1" dir="ltr">{fmtDate(new Date().toISOString().slice(0, 10))}</p>
        </div>
        <div className="px-8 py-2 rounded-full border-2 text-center whitespace-nowrap" style={{ borderColor: BLUE }}>
          <span className="font-arabic text-2xl leading-none align-middle" dir="rtl" style={{ unicodeBidi: 'isolate' }}>سجل التحصيل</span>
          <span className="mx-3 opacity-40">·</span>
          <span className="font-heading font-bold text-base uppercase tracking-wide align-middle">Payments Ledger</span>
        </div>
        <div className="text-sm w-44 text-right">
          <p className="font-semibold">Period</p>
          <p className="num text-[15px] mt-1" dir="ltr">{periodLabel}</p>
        </div>
      </div>

      <div className="mb-5 text-[15px]" style={{ color: BLUE }}>
        <span className="font-semibold text-sm">Grouped by:</span>{' '}
        <span className="font-bold text-slate-900">{blocks ? 'Collection Book (Date-wise)' : groupLabel}</span>
      </div>

      {blocks ? <BookBlocks blocks={blocks} grandTotal={grandTotal} currency={c.currency} /> : (
      <table className="w-full mb-6 border" style={{ borderColor: BLUE }}>
        <thead>
          <tr className="text-white text-left" style={{ backgroundColor: BLUE }}>
            <th className="px-3 py-2.5 text-sm font-semibold">{groupLabel}</th>
            {collectors.map((col) => (
              <th key={col} className="px-3 py-2.5 text-sm font-semibold text-right">{col}</th>
            ))}
            <th className="px-3 py-2.5 text-sm font-semibold text-right w-20">Pmts</th>
            <th className="px-3 py-2.5 text-sm font-semibold text-right w-32">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key} className={i % 2 ? 'bg-[#FDF4F6]' : ''} style={{ borderTop: '1px solid #dbe3f5' }} data-testid="print-ledger-row">
              <td className="px-3 py-2.5 text-sm font-medium whitespace-nowrap">{r.label}</td>
              {collectors.map((col) => (
                <td key={col} className="px-3 py-2.5 text-right num text-[14px] whitespace-nowrap">{r.collectors[col] ? fmtMoney(r.collectors[col], c.currency) : '—'}</td>
              ))}
              <td className="px-3 py-2.5 text-right num text-[14px]">{fmtNum(r.count)}</td>
              <td className="px-3 py-2.5 text-right num text-[15px] font-semibold whitespace-nowrap">{fmtMoney(r.total, c.currency)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-bold" style={{ borderColor: BLUE }}>
            <td className="px-3 py-2.5 text-[15px]">Totals · <span className="font-arabic">الإجمالي</span></td>
            {collectors.map((col) => (
              <td key={col} className="px-3 py-2.5 text-right num text-[14px] whitespace-nowrap">{fmtMoney(totals[col] || 0, c.currency)}</td>
            ))}
            <td className="px-3 py-2.5 text-right num text-[14px]">{fmtNum(rows.reduce((a, r) => a + r.count, 0))}</td>
            <td className="px-3 py-2.5 text-right num text-[16px] whitespace-nowrap">{fmtMoney(grandTotal, c.currency)}</td>
          </tr>
        </tbody>
      </table>
      )}
      {!blocks && !rows.length && <p className="text-center text-slate-400 text-sm mb-6">No payments in this period.</p>}

      <div className="flex justify-between items-end mt-10 text-sm" style={{ color: BLUE }}>
        <div className="text-center">
          <div className="border-t border-dotted border-slate-400 w-56 mb-1.5" />
          <p>Prepared by · <span className="font-arabic text-base">إعداد</span></p>
        </div>
        <div className="text-center">
          <div className="border-t border-dotted border-slate-400 w-56 mb-1.5" />
          <p>For {c.name} · <span className="font-arabic text-base">التوقيع</span></p>
        </div>
      </div>
    </div>
  );
}
